'use client';

import { createClient } from '@/lib/supabase/client';
import { filmService } from '@/lib/services/filmService';

export interface PlaybackSession {
  id: string;
  filmId: string;
  userId: string | null;
  endTimeSeconds: number;
  totalSecondsWatched: number;
  durationSeconds: number;
  countedAsView: boolean;
  createdAt: string;
}

export interface PlaybackAnalytics {
  totalMinutesWatched: number;
  sessionCount: number;
  sessions: PlaybackSession[];
  durationSeconds: number;
}

function mapSession(row: any): PlaybackSession {
  return {
    id: row.id,
    filmId: row.film_id,
    userId: row.user_id,
    endTimeSeconds: parseFloat(row.end_time_seconds) || 0,
    totalSecondsWatched: parseFloat(row.total_seconds_watched) || 0,
    durationSeconds: parseFloat(row.duration_seconds) || 0,
    countedAsView: row.counted_as_view || false,
    createdAt: row.created_at,
  };
}

export const playbackService = {
  /**
   * Save a playback session when the player is closed.
   * Only records if the user watched more than 30 seconds.
   * Also updates watch_history with progress_percent and completed flag.
   */
  async saveSession(params: {
    filmId: string;
    endTimeSeconds: number;
    totalSecondsWatched: number;
    durationSeconds: number;
  }): Promise<void> {
    // Only count if watched past 30 seconds
    const countedAsView = params.totalSecondsWatched > 30;

    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('playback_sessions')
      .insert({
        film_id: params.filmId,
        user_id: user?.id ?? null,
        end_time_seconds: Math.round(params.endTimeSeconds * 100) / 100,
        total_seconds_watched: Math.round(params.totalSecondsWatched * 100) / 100,
        duration_seconds: Math.round(params.durationSeconds * 100) / 100,
        counted_as_view: countedAsView,
      });

    if (error) {
      console.error('playbackService.saveSession error:', error.message);
    }

    // Update watch_history with completion % so Continue Watching and
    // personalized recommendations reflect the latest session.
    if (user && params.totalSecondsWatched > 0) {
      await filmService.updateWatchProgress(
        params.filmId,
        params.endTimeSeconds,
        params.durationSeconds,
      );
    }
  },

  /**
   * Fetch analytics for a specific film (admin use).
   */
  async getFilmAnalytics(filmId: string): Promise<PlaybackAnalytics> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('playback_sessions')
      .select('*')
      .eq('film_id', filmId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('playbackService.getFilmAnalytics error:', error.message);
      return { totalMinutesWatched: 0, sessionCount: 0, sessions: [], durationSeconds: 0 };
    }

    const sessions = (data || []).map(mapSession);
    const totalSecondsWatched = sessions.reduce((sum, s) => sum + s.totalSecondsWatched, 0);
    const totalMinutesWatched = Math.round((totalSecondsWatched / 60) * 10) / 10;
    const durationSeconds = sessions.length > 0
      ? Math.max(...sessions.map(s => s.durationSeconds).filter(d => d > 0))
      : 0;

    return {
      totalMinutesWatched,
      sessionCount: sessions.length,
      sessions,
      durationSeconds,
    };
  },
};
